import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, TreePine, Bird, Binoculars } from "lucide-react";

const RalamandalWildlifeSanctuary = () => {
  const navigate = useNavigate();
  const sanctuaryImage = "/Indore/nature/ralamandal0.jpg";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Ralamandal Wildlife Sanctuary",
    "description": "Beautiful wildlife sanctuary near Indore offering nature trails, bird watching, and peaceful environment",
    "image": sanctuaryImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Ralamandal Village",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 06:00-18:00",
    "priceRange": "₹10-20",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.4",
      "reviewCount": "3500"
    }
  };

  const highlights = [
    {
      icon: <TreePine className="w-6 h-6" />,
      title: "Dense Forest Cover",
      description: "Rich biodiversity with dense forests, perfect for nature walks and wildlife spotting"
    },
    {
      icon: <Bird className="w-6 h-6" />,
      title: "Bird Watching Paradise",
      description: "Home to numerous bird species, ideal destination for bird watching enthusiasts"
    },
    {
      icon: <Binoculars className="w-6 h-6" />,
      title: "Wildlife Spotting",
      description: "Spot various wildlife species including deer, peacocks, and other forest animals"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Nature Photography",
      description: "Perfect location for nature photography with scenic landscapes and wildlife"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "₹10 for Indians, ₹20 for cameras" },
    { label: "Timings", value: "6:00 AM - 6:00 PM (All days)" },
    { label: "Best Season", value: "October to March" },
    { label: "Duration", value: "3-4 hours" },
    { label: "Difficulty", value: "Easy to moderate trails" },
    { label: "Facilities", value: "Parking, Rest areas, Forest guides" }
  ];

  const wildlife = [
    { name: "Spotted Deer", status: "Common", bestTime: "Early morning/evening" },
    { name: "Peacocks", status: "Frequently seen", bestTime: "Morning hours" },
    { name: "Wild Boar", status: "Occasional", bestTime: "Evening" },
    { name: "Various Bird Species", status: "Abundant", bestTime: "Early morning" },
    { name: "Sambhar Deer", status: "Rare", bestTime: "Dawn/dusk" },
    { name: "Langur Monkeys", status: "Common", bestTime: "Throughout day" }
  ];

  const activities = [
    {
      title: "Nature Walks",
      description: "Explore well-marked trails through diverse forest ecosystems",
      duration: "1-3 hours",
      difficulty: "Easy"
    },
    {
      title: "Bird Watching",
      description: "Spot various resident and migratory bird species",
      duration: "2-4 hours",
      difficulty: "Easy"
    },
    {
      title: "Wildlife Photography",
      description: "Capture stunning photos of flora, fauna, and landscapes",
      duration: "2-5 hours",
      difficulty: "Moderate"
    },
    {
      title: "Picnicking",
      description: "Enjoy family time in designated picnic areas within the sanctuary",
      duration: "2-4 hours",
      difficulty: "Easy"
    }
  ];

  const nearbyAttractions = [
    { name: "Khajrana Ganesh Temple", distance: "15 km", time: "25 mins" },
    { name: "Indore Zoo", distance: "12 km", time: "20 mins" },
    { name: "Lal Bagh Palace", distance: "18 km", time: "30 mins" },
    { name: "Central Museum", distance: "16 km", time: "28 mins" }
  ];

  return (
    <>
      <Helmet>
        <title>Ralamandal Wildlife Sanctuary Indore - Nature & Wildlife | Aaoghumen</title>
        <meta name="description" content="Visit Ralamandal Wildlife Sanctuary near Indore with Aaoghumen - perfect for nature walks, bird watching, and wildlife photography. Complete guide with timings, wildlife info, and activities." />
        <meta name="keywords" content="ralamandal wildlife sanctuary, indore wildlife sanctuary, ralamandal bird watching, indore nature tourism, aaoghumen ralamandal, wildlife photography indore, ralamandal timings, indore forest" />
        
        <meta property="og:title" content="Ralamandal Wildlife Sanctuary - Nature Paradise near Indore | Aaoghumen" />
        <meta property="og:description" content="Explore Ralamandal Wildlife Sanctuary, a beautiful nature destination near Indore perfect for wildlife enthusiasts, bird watchers, and nature lovers." />
        <meta property="og:image" content={sanctuaryImage} />
        <meta name="twitter:image" content={sanctuaryImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/ralamandal-wildlife-sanctuary" />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/ralamandal-wildlife-sanctuary" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-green-600 via-emerald-500 to-teal-600 overflow-hidden">
          <img
            src={sanctuaryImage}
            alt="Ralamandal Wildlife Sanctuary"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-green-300">Ralamandal Wildlife Sanctuary</span>
              </h1>
              <p className="text-xl mb-6">
                Escape into nature at this beautiful wildlife sanctuary near Indore, offering pristine forests, diverse wildlife, and peaceful trails perfect for nature enthusiasts and families.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.4 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>6 AM - 6 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>₹10 Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Ralamandal Wildlife Sanctuary</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Ralamandal Wildlife Sanctuary is a hidden gem located just outside Indore, offering nature lovers and wildlife enthusiasts a perfect escape from urban life. This pristine sanctuary encompasses dense forests, grasslands, and diverse ecosystems that support a rich variety of flora and fauna. The sanctuary serves as an important habitat for numerous species and provides an excellent opportunity for visitors to experience the natural beauty of Madhya Pradesh's wildlife.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The sanctuary features well-maintained trails that wind through different forest sections, allowing visitors to explore various habitats and spot different species of wildlife. The area is particularly renowned for its bird population, making it a paradise for bird watchers and nature photographers. The peaceful environment and clean air make it an ideal destination for families looking to spend quality time in nature.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, discover the best trails for wildlife spotting, learn about the sanctuary's conservation efforts, and experience guided nature walks that enhance your understanding of the local ecosystem. The sanctuary offers a perfect blend of adventure, education, and relaxation in a natural setting that showcases the biodiversity of central India.
              </p>
            </div>
          </section>

          {/* Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Sanctuary Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-green-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-green-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Wildlife Spotting */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Wildlife Spotting Guide</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {wildlife.map((animal, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-green-600 mb-2">{animal.name}</h3>
                  <p className="text-gray-500 text-sm mb-2">{animal.status}</p>
                  <p className="text-gray-600 text-sm">Best time: {animal.bestTime}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Activities */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Activities & Experiences</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {activities.map((activity, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-green-600 mb-3">{activity.title}</h3>
                  <p className="text-gray-600 mb-4">{activity.description}</p>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Duration: {activity.duration}</span>
                    <span>Difficulty: {activity.difficulty}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Tips for Visitors */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Tips</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">What to Bring</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Comfortable walking shoes with good grip</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Water bottles and light snacks</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Binoculars for bird watching</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Camera with telephoto lens</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Best Practices</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span>Visit early morning for best wildlife spotting</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span>Maintain silence to avoid disturbing wildlife</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span>Follow designated trails only</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span>Don't feed or disturb wild animals</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-green-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> Local buses to Ralamandal village, then short walk</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Auto:</strong> Available from Indore city, ₹80-150</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Taxi:</strong> Most convenient option for groups</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Distance:</strong> 15 km from Indore city center</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Available at sanctuary entrance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Route:</strong> Well-marked roads via Ralamandal village</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Explore Nature's Paradise</h2>
              <p className="text-xl mb-6">Visit Ralamandal Wildlife Sanctuary with expert nature guides and wildlife insights</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Ralamandal Village, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-green-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Plan Nature Trip
                </button>
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-green-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default RalamandalWildlifeSanctuary;